import { useState, useMemo } from 'react'
import Select from 'react-select'
import DatePicker from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.css'
import { ChevronDown, ChevronUp, X } from 'lucide-react'

const TAG_OPTIONS = [
  { value: 'interested', label: 'Interested' },
  { value: 'not-interested', label: 'Not interested' },
  { value: 'meeting-booked', label: 'Meeting booked' },
  { value: 'auto-reply', label: 'Auto reply' },
  { value: 'ooo', label: 'Out of office' },
  { value: 'unsubscribe', label: 'Unsubscribe' },
  { value: 'question', label: 'Question' },
  { value: 'follow-up', label: 'Follow up' },
]
const SENTIMENT_OPTIONS = [
  { value: 'positive', label: 'Positive' },
  { value: 'neutral',  label: 'Neutral' },
  { value: 'negative', label: 'Negative' },
]

function ActiveChips({ value, onRemove, accountLabel }) {
  const chips = []
  if (value.tags.length) chips.push({ key: 'tags', label: `Tags: ${value.tags.join(', ')}` })
  if (value.sentiment.length) chips.push({ key: 'sentiment', label: `Sentiment: ${value.sentiment.join(', ')}` })
  if (value.accountId) chips.push({ key: 'accountId', label: `Account: ${accountLabel || value.accountId}` })
  if (value.dateFrom || value.dateTo) chips.push({ key: 'dates', label: `Date: ${value.dateFrom || '…'} → ${value.dateTo || '…'}` })
  if (value.hasReply !== null) chips.push({ key: 'hasReply', label: `Has reply: ${value.hasReply ? 'Yes' : 'No'}` })
  if (!chips.length) return null
  return (
    <div className="rh-active-chips" role="list">
      {chips.map(c => (
        <span key={c.key} className="rh-active-chip" role="listitem">
          {c.label}
          <button
            type="button"
            className="rh-active-chip__x"
            aria-label={`Remove filter ${c.label}`}
            onClick={() => onRemove(c.key)}
          >
            <X size={11} />
          </button>
        </span>
      ))}
    </div>
  )
}

export default function MoreFilters({ value, onChange, accounts, loadingAccounts }) {
  const [open, setOpen] = useState(false)

  const accountOptions = useMemo(
    () => (accounts || []).map(a => ({ value: a.id, label: a.email })),
    [accounts],
  )
  const accountLabel = accountOptions.find(o => o.value === value.accountId)?.label

  const activeCount = useMemo(() => {
    let n = 0
    if (value.tags.length) n++
    if (value.sentiment.length) n++
    if (value.accountId) n++
    if (value.dateFrom || value.dateTo) n++
    if (value.hasReply !== null) n++
    return n
  }, [value])

  const removeChip = (key) => {
    if (key === 'tags') onChange({ tags: [] })
    else if (key === 'sentiment') onChange({ sentiment: [] })
    else if (key === 'accountId') onChange({ accountId: null })
    else if (key === 'dates') onChange({ dateFrom: null, dateTo: null })
    else if (key === 'hasReply') onChange({ hasReply: null })
  }

  const clearAll = () => onChange({
    tags: [], sentiment: [], accountId: null,
    dateFrom: null, dateTo: null, hasReply: null,
  })

  const fromDate = value.dateFrom ? new Date(value.dateFrom) : null
  const toDate = value.dateTo ? new Date(value.dateTo) : null
  const fromInvalid = fromDate && toDate && fromDate > toDate

  const setFromDate = (d) => onChange({ dateFrom: d ? d.toISOString().slice(0, 10) : null })
  const setToDate   = (d) => onChange({ dateTo:   d ? d.toISOString().slice(0, 10) : null })

  return (
    <div className={`rh-more-filters ${open ? 'rh-more-filters--open' : ''}`}>
      <ActiveChips value={value} onRemove={removeChip} accountLabel={accountLabel} />
      <button
        type="button"
        className="rh-more-filters__toggle"
        aria-expanded={open}
        aria-controls="rh-more-filters-body"
        onClick={() => setOpen(o => !o)}
      >
        <span><strong>More filters</strong>{activeCount ? ` · ${activeCount} active` : ''}</span>
        {open ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
      </button>
      {open && (
        <div className="rh-more-filters__body" id="rh-more-filters-body">
          <div className="rh-mf-row">
            <label>
              <span>Tags</span>
              <Select
                isMulti
                options={TAG_OPTIONS}
                value={TAG_OPTIONS.filter(o => value.tags.includes(o.value))}
                onChange={(opts) => onChange({ tags: (opts || []).map(o => o.value) })}
                classNamePrefix="rh-rs"
                menuPortalTarget={document.body}
                styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
              />
            </label>
            <label>
              <span>Sentiment</span>
              <Select
                isMulti
                options={SENTIMENT_OPTIONS}
                value={SENTIMENT_OPTIONS.filter(o => value.sentiment.includes(o.value))}
                onChange={(opts) => onChange({ sentiment: (opts || []).map(o => o.value) })}
                classNamePrefix="rh-rs"
                menuPortalTarget={document.body}
                styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
              />
            </label>
          </div>

          {(loadingAccounts || accountOptions.length > 0) && (
            <div className="rh-mf-row">
              <label>
                <span>Account</span>
                <Select
                  isClearable
                  isLoading={loadingAccounts}
                  options={accountOptions}
                  value={accountOptions.find(o => o.value === value.accountId) || null}
                  onChange={(opt) => onChange({ accountId: opt ? opt.value : null })}
                  classNamePrefix="rh-rs"
                  menuPortalTarget={document.body}
                  styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                />
              </label>
              <label>
                <span>Has reply</span>
                <div className="rh-mf-radio-group">
                  <label><input type="radio" name="has_reply" checked={value.hasReply === null} onChange={() => onChange({ hasReply: null })} /> Any</label>
                  <label><input type="radio" name="has_reply" checked={value.hasReply === true} onChange={() => onChange({ hasReply: true })} /> Yes</label>
                  <label><input type="radio" name="has_reply" checked={value.hasReply === false} onChange={() => onChange({ hasReply: false })} /> No</label>
                </div>
              </label>
            </div>
          )}

          <div className="rh-mf-row">
            <label>
              <span>From date</span>
              <DatePicker
                selected={fromDate}
                onChange={setFromDate}
                selectsStart
                startDate={fromDate}
                endDate={toDate}
                dateFormat="yyyy-MM-dd"
                isClearable
                className={fromInvalid ? 'rh-mf-date rh-mf-date--invalid' : 'rh-mf-date'}
              />
            </label>
            <label>
              <span>To date</span>
              <DatePicker
                selected={toDate}
                onChange={setToDate}
                selectsEnd
                startDate={fromDate}
                endDate={toDate}
                minDate={fromDate || undefined}
                dateFormat="yyyy-MM-dd"
                isClearable
                className="rh-mf-date"
              />
            </label>
          </div>

          {fromInvalid && (
            <div className="rh-mf-error">From date must be on or before To date.</div>
          )}

          <div className="rh-mf-footer">
            <button type="button" className="rh-mf-clear" onClick={clearAll}>Clear all filters</button>
          </div>
        </div>
      )}
    </div>
  )
}
